/**
 * Theme. A palette is now described by three knobs — Accent + Base + light/dark
 * (the HeroUI model) — and the whole palette is DERIVED from them in oklab via
 * `@bunker/shared/theme`'s deriveTheme. The four shipped looks (dark / parchment
 * / eucalyptus / smoke) are presets: named (accent, base, mode) seeds. Users can
 * also dial a fully custom palette.
 *
 * Rendering: deriveTheme → applyDerivedPaletteVars writes the CSS variables the
 * app already reads, so no call site changes. The static blocks in index.css
 * become the no-JS fallback.
 *
 *   getTheme()           — representative preset id (drives THEME_IS_LIGHT)
 *   getSelection()       — full active selection (preset id or custom palette)
 *   setTheme(id)         — pick a preset
 *   setCustomPalette(p)  — set a custom accent/base/mode (+ brightness/cards)
 *   previewCustomPalette(p) — paint a palette without saving it (live drag)
 *   resetTheme()         — back to the default look, forgetting any choice
 *   toggleTheme()        — quick flip dark ↔ last light
 *   subscribeTheme(fn)   — listen for changes
 */
import {
  deriveTheme,
  contrastRatio,
  THEME_PRESETS,
  type ThemeMode,
  type ThemeInput,
  type DerivedPalette,
} from '@bunker/shared/theme';
import { applyDerivedPaletteVars } from '@/lib/themePaletteAdapter';

/** Bunker studio palette. Explicit user palettes still pass through the shared adapter. */
export const cispracsPalette: DerivedPalette = {
  mode: 'dark',
  isLight: false,
  canvas: '#191A1B',
  outer: '#141516',
  surface: '#202223',
  surfaceRaised: '#282A2C',
  surfaceSunken: '#161819',
  surfaceSecondary: '#252728',
  hover: '#303335',
  bubbleIn: '#252728',
  hairline: 'rgba(255,255,255,0.09)',
  ink: '#F4F4F1',
  inkMuted: '#B8BFC3',
  inkFaint: '#ADB5B9',
  accent: '#C9A477',
  accentHover: '#DAB78D',
  accentText: '#DAB78D',
  success: '#86BDA0',
  warning: '#D6B27A',
  danger: '#E58B86',
  secondary: '#C9A477',
  white: '#F4F4F1',
  overlayTint: '#F4F4F1',
};

/** Neutral daylight companion to the graphite studio theme. Keeping the two
 * core looks explicit prevents palette derivation from flattening the panel,
 * chat and settings surfaces into nearly the same grey. */
export const cispracsLightPalette: DerivedPalette = {
  mode: 'light',
  isLight: true,
  canvas: '#F7F8F9',
  outer: '#E9EDF0',
  surface: '#FFFFFF',
  surfaceRaised: '#FFFFFF',
  surfaceSunken: '#ECEFF1',
  surfaceSecondary: '#F3F5F6',
  hover: '#E8ECEF',
  bubbleIn: '#FFFFFF',
  hairline: 'rgba(29,36,40,0.10)',
  ink: '#20282D',
  inkMuted: '#5D686F',
  inkFaint: '#778289',
  accent: '#89633F',
  accentHover: '#755234',
  accentText: '#795633',
  success: '#358768',
  warning: '#A4773E',
  danger: '#BB5D55',
  secondary: '#89633F',
  white: '#FFFFFF',
  overlayTint: '#20282D',
};

/** Seed that reproduces the default cispracs look through deriveTheme — used to
 *  seed the Accent/Base sliders so they start from graphite + copper
 *  (not the HeroUI dark default) when cispracs is on and untouched. */
const CISPRACS_SEED: ThemeInput = {
  accent: cispracsPalette.accent,
  base: cispracsPalette.canvas,
  mode: 'dark',
};


export type Theme = 'dark' | 'parchment' | 'eucalyptus' | 'smoke';
export type { ThemeMode };

/** A user palette: accent + background colour + mode, and optionally how light
 *  the background is and how far cards step off it (see deriveTheme). */
export type CustomPalette = Omit<ThemeInput, 'keepAccent'>;

const isUnit = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 1;

/** Keep only what deriveTheme understands; a hand-edited or older entry must
 *  not be able to push NaN or an out-of-range knob into the palette. */
function sanitizePalette(raw: CustomPalette): CustomPalette {
  const out: CustomPalette = {
    accent: raw.accent,
    base: raw.base,
    mode: raw.mode === 'light' ? 'light' : 'dark',
  };
  if (isUnit(raw.brightness)) out.brightness = raw.brightness;
  if (isUnit(raw.cards)) out.cards = raw.cards;
  return out;
}

export type ThemeSelection =
  | { kind: 'preset'; id: Theme }
  | { kind: 'custom'; palette: CustomPalette };

const STORAGE_KEY = 'bunker.theme.v2';
const LEGACY_KEY = 'bunker.theme';
const LIGHT_KEY = 'bunker.theme.light'; // retired: last light preset; only wiped on reset
const EVENT = 'bunker:theme-change';

const ALL: readonly Theme[] = ['dark', 'parchment', 'eucalyptus', 'smoke'];

/** Seed (accent, base, mode) for each preset — single source of truth lives in
 *  @bunker/shared/theme so web + mobile agree. */
const PRESET_SEED: Record<Theme, ThemeInput> = (() => {
  const out = {} as Record<Theme, ThemeInput>;
  for (const p of THEME_PRESETS) {
    out[p.id as Theme] = { accent: p.accent, base: p.base, mode: p.mode };
  }
  return out;
})();

/** Which presets paint on light surfaces — drives the `.light`/`.dark` class
 *  and any `light ? … : …` branch a component needs. */
export const THEME_IS_LIGHT: Record<Theme, boolean> = {
  dark: PRESET_SEED.dark.mode === 'light',
  parchment: PRESET_SEED.parchment.mode === 'light',
  eucalyptus: PRESET_SEED.eucalyptus.mode === 'light',
  smoke: PRESET_SEED.smoke.mode === 'light',
};

export interface ThemeMeta {
  id: Theme;
  labelKey: string;
  /** Picker swatch colours: [canvas, surface, accent]. */
  swatch: [string, string, string];
}

export const THEME_OPTIONS: ThemeMeta[] = [
  { id: 'dark', labelKey: 'theme.darkShort', swatch: ['#191A1B', '#202223', '#c9a477'] },
  { id: 'parchment', labelKey: 'theme.parchment', swatch: ['#f7f8f9', '#ffffff', '#89633f'] },
  { id: 'eucalyptus', labelKey: 'theme.eucalyptus', swatch: ['#e8ece6', '#f4f7f2', '#3f7d63'] },
  { id: 'smoke', labelKey: 'theme.smoke', swatch: ['#eceaf3', '#f8f6fc', '#6c4bd1'] },
];

function isTheme(raw: string): raw is Theme {
  return (ALL as readonly string[]).includes(raw);
}

function readStored(): ThemeSelection {
  if (typeof window === 'undefined') return { kind: 'preset', id: 'dark' };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ThemeSelection;
      if (parsed.kind === 'preset' && isTheme(parsed.id)) return parsed;
      if (
        parsed.kind === 'custom' &&
        parsed.palette &&
        typeof parsed.palette.accent === 'string' &&
        typeof parsed.palette.base === 'string'
      ) {
        return { kind: 'custom', palette: sanitizePalette(parsed.palette) };
      }
    }
    // Migrate the old single-string value ('light' → parchment).
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) {
      if (isTheme(legacy)) return { kind: 'preset', id: legacy };
      if (legacy === 'light') return { kind: 'preset', id: 'parchment' };
    }
  } catch {
    /* fall through */
  }
  return { kind: 'preset', id: 'dark' };
}

let current: ThemeSelection | null = null;

function selection(): ThemeSelection {
  if (!current) current = readStored();
  return current;
}

export function getSelection(): ThemeSelection {
  return selection();
}

/** The active (accent, base, mode) seed — a preset's seed, or the custom values.
 *  Used to initialise the Accent/Base sliders. With no explicit choice, start
 *  from the default seed so the sliders show the current default accent. */
export function getActiveSeed(): ThemeInput {
  return seedOf(selection());
}

/** Server role/category colours require a full six-digit hex value. A saved
 * custom theme is device-local data, so keep invalid values out of requests. */
export function accentHexOr(fallback = '#C9A477'): string {
  const accent = getActiveSeed().accent;
  return /^#[0-9a-f]{6}$/i.test(accent) ? accent : fallback;
}

/** True when the user explicitly picked a theme/palette (vs the default). */
function hasStoredTheme(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) !== null || localStorage.getItem(LEGACY_KEY) !== null;
  } catch {
    return false;
  }
}

/** Contrast-safe colour for text/icons sitting ON the accent fill — near-black
 *  on a bright accent (cyan/yellow), white on a deep one (blue/purple). */
function onAccentText(hex: string): string {
  return contrastRatio('#151515', hex) >= contrastRatio('#ffffff', hex) ? '#151515' : '#ffffff';
}

function seedOf(sel: ThemeSelection): ThemeInput {
  if (sel.kind === 'custom') return sel.palette;
  if (sel.id === 'dark') return CISPRACS_SEED;
  if (sel.id === 'parchment') return { base: CISPRACS_SEED.base, accent: cispracsLightPalette.accent, mode: 'light' };
  return PRESET_SEED[sel.id];
}

/** Representative preset id — used by THEME_IS_LIGHT consumers. For a custom
 *  palette it collapses to a same-mode preset so light/dark branches still work. */
export function getTheme(): Theme {
  const sel = selection();
  if (sel.kind === 'preset') return sel.id;
  return sel.palette.mode === 'dark' ? 'dark' : 'parchment';
}

/** `explicit` — the user chose this (saved, or being previewed). Defaults to
 *  whether a choice is stored; a live preview passes `true` before saving. */
export function applySelection(sel: ThemeSelection, explicit = hasStoredTheme()): void {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  // A shared structure (panels, rail, controls) over a palette. Default: the
  // hand-tuned graphite + copper. An explicit theme/accent choice drives it
  // through deriveTheme instead.
  // The text colour on the accent is chosen by onAccentText below, so a
  // light accent is kept as picked instead of being darkened for white text.
  const palette = sel.kind === 'preset' && sel.id === 'parchment'
    ? cispracsLightPalette
    : sel.kind === 'preset' && sel.id === 'dark'
      ? cispracsPalette
      : explicit ? deriveTheme({ ...seedOf(sel), keepAccent: true }) : cispracsPalette;
  applyDerivedPaletteVars(palette);
  // Soft lift for floating overlays (the adapter's card shadow is a hairline).
  el.style.setProperty('--shadow-card', palette.isLight
    ? '0 12px 32px -16px rgb(25 36 42 / 0.22)'
    : '0 10px 30px -12px rgb(0 0 0 / 0.65)');
  // Contrast-correct text on the accent fill (follows the chosen accent).
  el.style.setProperty('--accent-foreground', onAccentText(palette.accent));
  const light = palette.isLight;
  el.dataset.theme = explicit ? (sel.kind === 'preset' ? sel.id : 'custom') : 'dark';
  el.classList.toggle('dark', !light);
  el.classList.toggle('light', light);
}

/** Back-compat: apply a preset by id (kept for existing callers). */
export function applyTheme(theme: Theme): void {
  applySelection({ kind: 'preset', id: theme });
}

function persist(sel: ThemeSelection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
  } catch {
    /* private mode — in-memory only for this tab */
  }
}

function commit(sel: ThemeSelection): void {
  current = sel;
  // Persist BEFORE applying: the cispracs branch of applySelection reads
  // hasStoredTheme() from localStorage to decide default-vs-chosen palette, so
  // the new selection must already be on disk when it runs.
  persist(sel);
  applySelection(sel);
  window.dispatchEvent(new CustomEvent<ThemeSelection>(EVENT, { detail: sel }));
}

export function setTheme(id: Theme): void {
  const sel = selection();
  if (sel.kind === 'preset' && sel.id === id) return;
  commit({ kind: 'preset', id });
}

export function setCustomPalette(palette: CustomPalette): void {
  commit({ kind: 'custom', palette: sanitizePalette(palette) });
}

/** Paint a palette without saving or broadcasting it — for a slider or colour
 *  area being dragged, where writing storage and re-rendering every listener on
 *  each pointer move made the picker stutter. Follow with setCustomPalette. */
export function previewCustomPalette(palette: CustomPalette): void {
  const sel: ThemeSelection = { kind: 'custom', palette: sanitizePalette(palette) };
  current = sel;
  applySelection(sel, true);
}

/** Forget any chosen theme or palette and return to the default look (the
 *  graphite + copper studio palette). Without
 *  this, trying «Своя палитра» once meant never getting the default back. */
export function resetTheme(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(LEGACY_KEY);
    localStorage.removeItem(LIGHT_KEY);
  } catch {
    /* private mode */
  }
  const sel: ThemeSelection = { kind: 'preset', id: 'dark' };
  current = sel;
  applySelection(sel, false);
  window.dispatchEvent(new CustomEvent<ThemeSelection>(EVENT, { detail: sel }));
}

/** Paint the default look without forgetting the saved choice — the live
 *  counterpart of resetTheme, for a preview that lands on «Графит» + copper. */
export function previewDefaultTheme(): void {
  const sel: ThemeSelection = { kind: 'preset', id: 'dark' };
  current = sel;
  applySelection(sel, false);
}

/** The default look's seed (graphite + copper), for editors to compare against. */
export const DEFAULT_SEED: ThemeInput = CISPRACS_SEED;

/** True while the user has made any explicit theme/palette choice. */
export function hasThemeChoice(): boolean {
  return hasStoredTheme();
}

/** Quick flip: dark ↔ light. Flips the MODE while PRESERVING the chosen accent
 *  (a custom palette), so light/dark doesn't drop a custom accent. */
export function toggleTheme(): Theme {
  const sel = selection();
  if (sel.kind === 'preset' && (sel.id === 'dark' || sel.id === 'parchment')) {
    const next = sel.id === 'dark' ? 'parchment' : 'dark';
    setTheme(next);
    return next;
  }
  // Keep the chosen background tone, accent and card contrast; only the mode
  // (and therefore the brightness range) flips.
  const seed = getActiveSeed();
  const goingLight = seed.mode !== 'light';
  setCustomPalette({ accent: seed.accent, base: seed.base, mode: goingLight ? 'light' : 'dark', cards: seed.cards });
  return goingLight ? 'parchment' : 'dark';
}

export function subscribeTheme(fn: (theme: Theme) => void): () => void {
  const handler = () => fn(getTheme());
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}

/** Apply the stored theme synchronously at boot (no flash). */
export function hydrateTheme(): void {
  applySelection(selection());
}
